import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import '@/App.css';
import { ContentProvider, useContent } from './context/ContentContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { ModalProvider } from './context/ModalContext';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ProductsSection from './components/ProductsSection';
import ContactSection from './components/ContactSection';
import NewsSection from './components/NewsSection';
import NewsPage from './components/NewsPage';
import Footer from './components/Footer';
import Modal from './components/Modal';
import { getMenuConfig } from './lib/menuConfig';
import AdminApp from './admin/AdminApp';

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const scrollToHash = () => {
      const element = document.querySelector(location.hash);
      if (!element) return false;

      const targetTop = element.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      return true;
    };

    if (scrollToHash()) return;

    const frameId = window.requestAnimationFrame(scrollToHash);
    const timeoutId = window.setTimeout(scrollToHash, 150);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname, location.hash]);

  return null;
}

function Providers({ children }) {
  return (
    <ContentProvider>
      <ThemeProvider>
        <LanguageProvider>
          <ModalProvider>
            {children}
          </ModalProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ContentProvider>
  );
}

function MainWebsite() {
  const { siteContent, loading, error, refreshContent } = useContent();

  if (loading && !siteContent) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <div className="text-center">
          <div className="mx-auto mb-5 h-10 w-10 rounded-full border-2 border-[#D4AF37]/30 border-t-[#D4AF37] animate-spin" />
          <p className="text-sm uppercase tracking-[0.2em] text-white/50">Loading content</p>
        </div>
      </div>
    );
  }

  if (error && !siteContent) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-bold mb-3">Backend unavailable</h1>
          <p className="text-white/60 text-sm mb-6">The website content could not be loaded from the backend.</p>
          <button
            type="button"
            onClick={() => refreshContent().catch(() => {})}
            className="px-5 py-3 bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[#C5A028] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const menuConfig = getMenuConfig(siteContent?.global?.menu);

  return (
    <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
      <ScrollToHash />
      <Header />
      <main>
        {menuConfig.home?.enabled !== false && <HeroSection />}
        {menuConfig.about?.enabled !== false && <AboutSection />}
        {menuConfig.products?.enabled !== false && <ProductsSection />}
        {menuConfig.contact?.enabled !== false && <ContactSection />}
      </main>
      <Footer />
      <Modal />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<AdminApp />} />
        <Route path="/news" element={<Providers><NewsPage /></Providers>} />
        <Route path="/*" element={<Providers><MainWebsite /></Providers>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
